import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Calendar, Clock, Copy, Check, RefreshCw } from 'lucide-react';
import { availabilityApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface AvailabilitySetting {
  id: string;
  weekday: number;
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
  is_active: boolean;
  location_type: string;
}

const weekdayNames = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const weekdayShort = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const locationLabels: Record<string, string> = {
  in_person: 'Vor Ort',
  online: 'Online',
  both: 'Beides',
};

export default function AvailabilitySettings() {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<AvailabilitySetting[]>([]);
  const [slug, setSlug] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [slugSaved, setSlugSaved] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<{ created: number; skipped: number } | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    loadSettings();
    loadSlug();
    // Set default date range: today + 4 weeks
    const today = new Date();
    const fourWeeks = new Date(today);
    fourWeeks.setDate(fourWeeks.getDate() + 28);
    setFromDate(today.toISOString().split('T')[0]);
    setToDate(fourWeeks.toISOString().split('T')[0]);
  }, []);

  const loadSettings = async () => {
    try {
      const res = await availabilityApi.list();
      setSettings(res.data);
    } catch { /* ignore */ }
  };

  const loadSlug = async () => {
    try {
      const res = await availabilityApi.getSlug();
      setSlug(res.data.slug || '');
      setSlugInput(res.data.slug || '');
    } catch { /* ignore */ }
  };

  const saveSlug = async () => {
    setSlugError('');
    setSlugSaved(false);
    try {
      const res = await availabilityApi.updateSlug(slugInput);
      setSlug(res.data.slug);
      setSlugSaved(true);
    } catch (err: any) {
      setSlugError(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const deleteSetting = async (id: string) => {
    await availabilityApi.delete(id);
    loadSettings();
  };

  const handleGenerate = async () => {
    if (!fromDate || !toDate) return;
    setGenerating(true);
    setGenerateResult(null);
    try {
      const res = await availabilityApi.generateSlots({ from_date: fromDate, to_date: toDate });
      setGenerateResult(res.data);
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler bei der Slot-Generierung', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const bookingUrl = slug ? `${window.location.origin}/buchen/${slug}` : '';

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Booking Link / Slug */}
      <div className="card" style={{ padding: 32, marginBottom: 24 }}>
        <h3 style={{ marginBottom: 12 }}>
          <Calendar size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Buchungs-Link
        </h3>
        <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
          Teile diesen Link mit Neukunden, damit sie direkt Termine buchen können — ohne Login.
        </p>

        <div className="form-group">
          <label>Dein Buchungs-Slug</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0 }}>
              <span style={{
                padding: '8px 12px', background: 'var(--bg)', borderRadius: '6px 0 0 6px',
                border: '1px solid var(--border, #d5cdc0)', borderRight: 'none', fontSize: '0.85rem', color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
              }}>
                /buchen/
              </span>
              <input
                value={slugInput}
                onChange={(e) => { setSlugInput(e.target.value); setSlugSaved(false); setSlugError(''); }}
                placeholder="sara-wilhelm"
                style={{ borderRadius: '0 6px 6px 0', flex: 1 }}
              />
            </div>
            <button className="btn btn-primary" onClick={saveSlug} disabled={!slugInput || slugInput === slug}>
              {slugSaved ? <><Check size={16} /> Gespeichert</> : 'Speichern'}
            </button>
          </div>
          {slugError && <p style={{ color: 'var(--danger-text, #991b1b)', fontSize: '0.82rem', marginTop: 4 }}>{slugError}</p>}
        </div>

        {bookingUrl && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center', marginTop: 12,
            padding: '10px 14px', background: 'var(--bg)', borderRadius: 8,
          }}>
            <input readOnly value={bookingUrl} style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.82rem' }} />
            <button className="btn btn-sm btn-secondary" onClick={() => {
              navigator.clipboard.writeText(bookingUrl);
              showToast('Link kopiert!', 'success');
            }}>
              <Copy size={14} /> Kopieren
            </button>
          </div>
        )}
      </div>

      {/* Weekly Availability */}
      <div className="card" style={{ padding: 32, marginBottom: 24 }}>
        <div className="flex-between" style={{ marginBottom: 16 }}>
          <div>
            <h3 style={{ marginBottom: 4 }}>
              <Clock size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Wöchentliche Verfügbarkeit
            </h3>
            <p className="text-muted text-sm">
              Definiere deine wiederkehrenden Arbeitszeiten. Daraus werden Buchungs-Slots generiert.
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
            <Plus size={16} /> Hinzufügen
          </button>
        </div>

        {settings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 20px', color: 'var(--text-muted)' }}>
            <Clock size={28} style={{ marginBottom: 8, opacity: 0.4 }} />
            <p className="text-sm">Noch keine Verfügbarkeiten definiert.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2, 3, 4, 5, 6].map((day) => {
              const daySettings = settings.filter((s) => s.weekday === day);
              if (daySettings.length === 0) return null;
              return (
                <div key={day} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', background: 'var(--bg)', borderRadius: 8,
                }}>
                  <span style={{ fontWeight: 600, minWidth: 90, fontSize: '0.9rem' }}>
                    {weekdayNames[day]}
                  </span>
                  <div style={{ display: 'flex', gap: 8, flex: 1, flexWrap: 'wrap' }}>
                    {daySettings.map((s) => (
                      <div key={s.id} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '4px 10px', background: s.is_active ? 'var(--success-bg, #e8f5e9)' : 'var(--bg-muted, #eee)',
                        borderRadius: 6, fontSize: '0.85rem',
                      }}>
                        <span>{s.start_time} – {s.end_time}</span>
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                          ({s.slot_duration_minutes}min, {locationLabels[s.location_type] || s.location_type})
                        </span>
                        <button
                          className="btn-icon"
                          onClick={() => deleteSetting(s.id)}
                          title="Löschen"
                          style={{ padding: 2 }}
                        >
                          <Trash2 size={13} color="var(--danger-text, #991b1b)" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Generate Slots */}
      {settings.length > 0 && (
        <div className="card" style={{ padding: 32 }}>
          <h3 style={{ marginBottom: 4 }}>
            <RefreshCw size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Slots generieren
          </h3>
          <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
            Erstelle konkrete Buchungs-Slots basierend auf deinen Verfügbarkeits-Einstellungen.
            Bereits vorhandene Slots werden nicht doppelt angelegt.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label>Von</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Bis</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={generating || !fromDate || !toDate}
          >
            <RefreshCw size={16} /> {generating ? 'Generiere...' : 'Slots generieren'}
          </button>

          {generateResult && (
            <div style={{
              background: 'var(--success-bg, #e8f5e9)', color: 'var(--success-text, #2e7d32)', padding: '10px 16px',
              borderRadius: 8, marginTop: 12, fontSize: '0.9rem',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Check size={16} />
              {generateResult.created} Slots erstellt, {generateResult.skipped} übersprungen (bereits vorhanden)
            </div>
          )}
        </div>
      )}

      {/* Add Setting Modal */}
      {showAddForm && (
        <AddSettingForm
          onClose={() => setShowAddForm(false)}
          onSaved={loadSettings}
        />
      )}
    </div>
  );
}


function AddSettingForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { showToast } = useToast();
  const [weekday, setWeekday] = useState(0);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [duration, setDuration] = useState(60);
  const [locationType, setLocationType] = useState('both');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await availabilityApi.create({
        weekday,
        start_time: startTime,
        end_time: endTime,
        slot_duration_minutes: duration,
        location_type: locationType,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Verfügbarkeit hinzufügen</h2>
          <button className="btn-icon" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Wochentag *</label>
            <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
              {weekdayShort.map((name, i) => (
                <option key={i} value={i}>{weekdayNames[i]}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Von *</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Bis *</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Slot-Dauer (Minuten)</label>
              <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                <option value={30}>30 Min</option>
                <option value={45}>45 Min</option>
                <option value={60}>60 Min</option>
                <option value={90}>90 Min</option>
                <option value={120}>120 Min</option>
              </select>
            </div>
            <div className="form-group">
              <label>Ort</label>
              <select value={locationType} onChange={(e) => setLocationType(e.target.value)}>
                <option value="both">Vor Ort oder Online</option>
                <option value="in_person">Nur Vor Ort</option>
                <option value="online">Nur Online</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Speichern...' : 'Hinzufügen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
